import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Printer, ArrowLeft } from "lucide-react";
import { format } from "date-fns";

interface Receipt {
  receiptNumber: string;
  enrollmentId: string;
  courseId: string;
  courseName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  paymentId: string;
  status: string;
  createdAt: string;
  metadata: {
    paymentIntentId: string;
    customerId: string;
  };
}

const PaymentReceipt = () => {
  const { receiptId } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!receiptId) return;

      try {
        const db = getFirestore();
        const receiptDoc = await getDoc(doc(db, "receipts", receiptId));
        
        if (receiptDoc.exists()) {
          setReceipt(receiptDoc.data() as Receipt);
        }
      } catch (error) {
        console.error("Error fetching receipt:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [receiptId]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    if (!receipt) return;

    const receiptContent = `
      Receipt Number: ${receipt.receiptNumber}
      Date: ${format(new Date(receipt.createdAt), "PPP")}
      Course: ${receipt.courseName}
      Amount: GBP ${Number(receipt.amount).toFixed(2)}
      Payment Method: ${receipt.paymentMethod}
      Status: ${receipt.status}
      Payment ID: ${receipt.paymentId}
    `;

    const blob = new Blob([receiptContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipt-${receipt.receiptNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Loading receipt...</div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Receipt Not Found</h2>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>
      
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Payment Receipt</CardTitle>
          <CardDescription>Receipt Number: {receipt.receiptNumber}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">
                  {format(new Date(receipt.createdAt), "PPP")}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium capitalize">{receipt.status}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Course Details</h3>
              <p>{receipt.courseName}</p>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-medium">
                    ${'\u00A3'}${Number(receipt.amount).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="font-medium capitalize">{receipt.paymentMethod}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-medium mb-2">Transaction Details</h3>
              <p className="text-sm text-gray-500">Payment ID</p>
              <p className="font-mono text-sm">{receipt.paymentId}</p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentReceipt; 